import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { murmurhash } from '../../../../../lib/js/hash';
import LinkShape from '../../../../../lib/shapes/LinkShape';
import ImageShape from '../../../../../lib/shapes/ImageShape';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableModifier,
  TableCellContactButtons,
  TableCellPhone,
} from '../../../../organisms/Table';
import { Namespace } from '../../../../../translation/namespace';
import { TextMarker } from '../../../../../_lib/components/TextMarker/TextMarker';
import { ProfilePictureModifier, ProfilePictureName } from '../../../../../_lib/components/ProfilePictureName';

const ALL_COLUMNS = {
  PROFILE_LINK: 'profileLink',
  PHONE_LINK: 'phoneLink',
  INSTITUTION: 'institution',
  ORG_UNIT: 'orgUnit',
  MATCHING_FIELDS: 'matchingFields',
  EMAIL_LINK: 'emailLink',
};

function UserSearchResultTable({ items }) {
  const { t } = useTranslation(Namespace.UserSearchResults);
  const nameLabel = t('label.person');
  const institutionLabel = t('label.institution');
  const orgUnitLabel = t('label.orgUnit');
  const phoneLabel = t('label.phoneNumber');
  const emailLabel = t('label.emailAddress');
  const matchingFieldsLabel = t('label.matchingFields');
  const caption = t('caption');

  /**
   * Takes an user and corrects all it's invalid attribute
   * @param user
   */
  const processUser = (user) => {
    const toReturn = {};

    toReturn.phone = {
      url: user.phoneLink ? user.phoneLink.url : null,
      number: user.phoneLink ? user.phoneLink.name : null,
    };

    toReturn.matchinFields = (user.matchingFields || []).map((fieldName) => {
      return t(`fieldNames.${fieldName}`);
    });

    toReturn.email = user.emailLink ? user.emailLink.url : null;

    return Object.assign(user, toReturn);
  };

  const tableRowKeys = (tableRow, key) => {
    return Object.keys(tableRow).includes(key);
  };

  const createHeaders = (existingColumns) => {
    return (
      <TableRow>
        {existingColumns.includes(ALL_COLUMNS.PROFILE_LINK) && (
          <TableCell modifier={TableModifier.LWIDTH}>{nameLabel}</TableCell>
        )}
        {existingColumns.includes(ALL_COLUMNS.PHONE_LINK) && <TableCell>{phoneLabel}</TableCell>}
        {existingColumns.includes(ALL_COLUMNS.INSTITUTION) && <TableCell>{institutionLabel}</TableCell>}
        {existingColumns.includes(ALL_COLUMNS.ORG_UNIT) && <TableCell>{orgUnitLabel}</TableCell>}
        {existingColumns.includes(ALL_COLUMNS.MATCHING_FIELDS) && (
          <TableCell modifier={TableModifier.SWIDTH}>{matchingFieldsLabel}</TableCell>
        )}
        {existingColumns.includes(ALL_COLUMNS.EMAIL_LINK) && (
          <TableCell modifier={TableModifier.XXSWIDTH}>
            <span className="invisible">{emailLabel}</span>
          </TableCell>
        )}
      </TableRow>
    );
  };

  const extractRows = (existingColumns) => {
    return items.map((u) => {
      const user = processUser(u);

      return (
        <TableRow key={`user-${murmurhash(user)}`}>
          {existingColumns.includes(ALL_COLUMNS.PROFILE_LINK) && (
            <TableCell dataHead={nameLabel}>
              <ProfilePictureName {...user} modifier={ProfilePictureModifier.ALIGN_TOP} highlight />
            </TableCell>
          )}
          {existingColumns.includes(ALL_COLUMNS.PHONE_LINK) && (
            <TableCellPhone label={phoneLabel} name={user.phone.number} url={user.phone.url} />
          )}
          {existingColumns.includes(ALL_COLUMNS.INSTITUTION) && (
            <TableCell dataHead={institutionLabel}>
              <TextMarker text={user.institution} />
            </TableCell>
          )}
          {existingColumns.includes(ALL_COLUMNS.ORG_UNIT) && (
            <TableCell dataHead={orgUnitLabel}>
              <TextMarker text={user.orgUnit} />
            </TableCell>
          )}
          {existingColumns.includes(ALL_COLUMNS.MATCHING_FIELDS) && (
            <TableCell dataHead={matchingFieldsLabel}>{user.matchinFields.join(', ')}</TableCell>
          )}
          {existingColumns.includes(ALL_COLUMNS.EMAIL_LINK) && (
            <TableCellContactButtons
              label={emailLabel}
              emailUrl={user.email}
              phoneUrl={user.phone.url}
              personName={user.profileName}
            />
          )}
        </TableRow>
      );
    });
  };

  const getExistingColumns = (items) => {
    const existingColumns = [];
    const allColumns = Object.values(ALL_COLUMNS);

    allColumns.forEach((column) => {
      items.forEach((tableRow) => {
        if (tableRowKeys(tableRow, column)) {
          if (!existingColumns.includes(column)) {
            existingColumns.push(column);
          }
        }
      });
    });
    return existingColumns;
  };

  const existingColumns = getExistingColumns(items);

  return (
    <Table caption={caption} removeOnPaste>
      <TableHead>{createHeaders(existingColumns)}</TableHead>
      <TableBody>{extractRows(existingColumns)}</TableBody>
    </Table>
  );
}

UserSearchResultTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      userName: PropTypes.string,
      matchingFields: PropTypes.arrayOf(PropTypes.string),
      profileName: PropTypes.string,
      institution: PropTypes.string,
      orgUnit: PropTypes.string,
      profileImageUrl: ImageShape,
      profileLink: LinkShape,
      emailLink: LinkShape,
      phoneLink: LinkShape,
    }),
  ).isRequired,
};

export default UserSearchResultTable;
